import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { getHeader } from "../../utils/util";

export default function ContactTableDynamic({
  apiUrl,
  idField = "_id",
  onChange,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const hasUserInteracted = useRef(false); // Track if user has interacted

  /* ───────────── fetch data ───────────── */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(apiUrl, getHeader())
      .then(({ data }) => {
        if (alive) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("ContactTableDynamic fetch error:", e);
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [apiUrl]);

  /* ─────────── selection logic ─────────── */
  const selectedRows = useMemo(() => {
    return selectedIds.length === 0
      ? []
      : rows.filter((row) => selectedIds.includes(row[idField]));
  }, [rows, selectedIds, idField]);

  const columnKeys = useMemo(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0]).filter((k) => k !== idField); // hide id column
  }, [rows, idField]);

  /* ─────────── notify parent ─────────── */
  useEffect(() => {
    // Only call onChange if:
    // 1. There's an onChange handler
    // 2. User has interacted with checkboxes
    // 3. The selection actually changed
    if (!onChange || !hasUserInteracted.current) return;

    onChange(selectedRows);
  }, [selectedRows, onChange]);

  /* ─────────── checkbox handlers ─────────── */
  const toggleAll = useCallback(
    (e) => {
      hasUserInteracted.current = true;
      setSelectedIds(e.target.checked ? rows.map((r) => r[idField]) : []);
    },
    [rows, idField]
  );

  const toggleOne = useCallback((id) => {
    hasUserInteracted.current = true;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ mt: 3, textAlign: "center" }}>
        Please select contact group with contacts list
      </Typography>
    );
  }

  const allIds = rows.map((r) => r[idField]);
  const allSelected = selectedIds.length === allIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  return (
    <TableContainer
      sx={{ maxHeight: 300 }}
      component={Paper}
      variant="outlined"
      elevation={0}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={toggleAll}
              />
            </TableCell>
            {columnKeys.map((key) => (
              <TableCell key={key}>{key.replace(/_/g, " ")}</TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => {
            const rowId = row[idField];
            const isChecked = selectedIds.includes(rowId);
            return (
              <TableRow hover key={rowId}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleOne(rowId)}
                  />
                </TableCell>
                {columnKeys.map((key) => (
                  <TableCell key={key}>{row[key] ?? ""}</TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
